// Who the next match is against, as the first row of the caster select
// chooses it: Play is the AI, Invite makes a room, and Join takes the code of
// a room. Added 2026-10-14. Garrett: "design a flow for creating a
// multiplayer match right from that first wizard screen", and of the first
// word: "don't say 'The AI' -- say 'Play' there".
// In a room the row follows the seat: the host is on Invite, and a guest or a
// spectator is on Join. Play leaves the room. The picks made before the room
// existed go to the room when it answers.

import type { CasterId } from '../content/casters';
import { createStore } from '../lib/store';
import { readRoomCode } from '../room/protocol';
import { createRoom, joinRoom, leaveRoom, pickRoomCaster } from './room';
import { roomStore } from './roomStore';
import { casterSelectStore } from './setup';

export type Opponent = 'play' | 'invite' | 'join';

export type OpponentState = {
  /** The row as chosen when the screen is in no room. */
  chosen: Opponent;
  /** A room is being made. */
  making: boolean;
  /** The last try to make a room failed. */
  failed: boolean;
  /** The code typed in Join, as typed. */
  typed: string;
};

export const opponentStore = createStore<OpponentState>({
  chosen: 'play',
  making: false,
  failed: false,
  typed: '',
});

/** Picks to send to the room when it answers. */
let carried: readonly CasterId[] = [];

/** The row as it shows now. In a room the seat decides it. */
export const currentOpponent = (): Opponent => {
  const room = roomStore.get();
  if (!room) return opponentStore.get().chosen;
  return room.seat === 'host' ? 'invite' : 'join';
};

const update = (change: Partial<OpponentState>): void =>
  opponentStore.set((state) => ({ ...state, ...change }));

/** The screen leaves its room, and the select stays open for a match against the AI. */
const leaveForSelect = (): void => {
  if (!roomStore.get()) return;
  leaveRoom();
  casterSelectStore.set(true);
};

/** Asks the server for a room. A failure stays on Invite, and `makeRoomAgain` asks once more. */
const makeRoom = async (picks: readonly CasterId[]): Promise<void> => {
  update({ making: true, failed: false });
  carried = picks;
  const code = await createRoom();
  update({ making: false, failed: code === null });
};

export const chooseOpponent = async (next: Opponent, picks: readonly CasterId[]): Promise<void> => {
  if (next === currentOpponent()) return;
  leaveForSelect();
  update({ chosen: next, failed: false });
  if (next === 'invite') await makeRoom(picks);
};

export const makeRoomAgain = (picks: readonly CasterId[]): void => {
  if (roomStore.get() || opponentStore.get().making) return;
  void makeRoom(picks);
};

export const typeCode = (text: string): void => update({ typed: text });

/** The code typed is a code. */
export const typedCode = (): string | null => readRoomCode(opponentStore.get().typed);

export const joinTypedCode = (picks: readonly CasterId[]): void => {
  const code = typedCode();
  if (!code) return;
  carried = picks;
  joinRoom(code);
};

// A screen that left its room, from the menu or from a notice, chooses the AI again.
let wasInRoom = roomStore.get() !== null;
roomStore.subscribe((room) => {
  if (wasInRoom && room === null) update({ chosen: 'play', making: false, typed: '' });
  wasInRoom = room !== null;
});

// The picks made before the room answered go to it once this screen has a seat and no picks there.
roomStore.subscribe((room) => {
  const seat = room?.seat;
  const view = room?.view;
  if (!seat || !view || view.phase !== 'lobby' || carried.length === 0) return;
  if (view.picks[seat].length === 0) for (const caster of carried) pickRoomCaster(caster);
  carried = [];
});
